import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const polarToCartesian = (cx, cy, r, angle) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(rad),
        y: cy + r * Math.sin(rad)
    };
};

const describeArc = (cx, cy, r, start, end) => {
    const s = polarToCartesian(cx, cy, r, end);
    const e = polarToCartesian(cx, cy, r, start);
    const large = end - start <= 180 ? 0 : 1;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 0 ${e.x} ${e.y}`;
};

export default function PressureGauge({
    value = 5.1,
    scaleMin = -15,
    scaleMax = 15,
    scaleUnit = 'psi',
    step = 5,
    size = 300,
    displayLabel = '',
    displayColor = '#202940'
}) {
    const cx = size / 2;
    const cy = size / 2;
    const radius = size * 0.38;

    const v = clamp(value, scaleMin, scaleMax);
    const percent = (v - scaleMin) / (scaleMax - scaleMin);

    const startAngle = -90;
    const endAngle = 90;
    const needleAngle = startAngle + percent * (endAngle - startAngle);

    const needle = polarToCartesian(cx, cy, radius - 10, needleAngle);

    const ticks = useMemo(() => {
        const arr = [];
        for (let i = scaleMin; i <= scaleMax; i += step) arr.push(i);
        return arr;
    }, [scaleMin, scaleMax, step]);

    // Use React state for hover
    const [hovered, setHovered] = React.useState(false);
    const dimTextZoom = hovered ? 1.2 : 1;
    const dimColor = hovered ? (displayColor ?? "#FFE06C") : "#fff";
    const theme = useTheme();
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div
                style={{
                    width: size + 100,
                    background: theme.palette.background.default,
                    borderRadius: 18,
                    padding: 16,
                    textAlign: "center",
                    color: theme.palette.text.primary
                }}
            >
                <svg
                    width={size}
                    height={size * 0.65}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    {/* Background arc */}
                    <path
                        d={describeArc(cx, cy, radius, startAngle, endAngle)}
                        stroke="#b6bce2"
                        strokeWidth="18"
                        fill="none"
                        strokeLinecap="round"
                    />

                    {/* Active arc */}
                    <path
                        d={describeArc(
                            cx,
                            cy,
                            radius,
                            startAngle,
                            startAngle + percent * (endAngle - startAngle)
                        )}
                        stroke="url(#grad)"
                        strokeWidth="18"
                        fill="none"
                        strokeLinecap="round"
                    />

                    {/* Gradient */}
                    <defs>
                        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={displayColor} />
                            <stop offset="60%" stopColor={displayColor} />
                            <stop offset="100%" stopColor={displayColor} />
                        </linearGradient>
                    </defs>

                    {/* Labels */}
                    {ticks.map((t) => {
                        const pct = (t - scaleMin) / (scaleMax - scaleMin);
                        const angle = startAngle + pct * (endAngle - startAngle);
                        const pos = polarToCartesian(cx, cy, radius + 22, angle);

                        let anchor = "middle";
                        if (angle > 60) anchor = "start";
                        if (angle < -60) anchor = "end";

                        return (
                            <text
                                key={t}
                                x={pos.x}
                                y={pos.y}
                                textAnchor={anchor}
                                dominantBaseline="middle"
                                // fontSize="11"
                                fontSize={11 * dimTextZoom}
                                fontWeight={hovered ? "bold" : undefined}
                                fill={dimColor}
                            // fill="#fff"
                            >
                                {t}
                            </text>
                        );
                    })}

                    {/* Needle */}
                    <line
                        x1={cx}
                        y1={cy}
                        x2={needle.x + 2}
                        y2={needle.y + 10}
                        stroke="#22c55e"
                        strokeWidth="3"
                    />
                    <circle cx={cx} cy={cy} r="6" fill="#b6bce2" />
                </svg>

                {/* Value */}
                <div style={{ fontSize: 28, fontWeight: 600, color: dimColor }}>
                    {v.toFixed(2)} {scaleUnit.toUpperCase()}
                </div>
                {/* <div style={{ fontSize: 12, opacity: 0.7 }}>{scaleUnit.toUpperCase()}</div> */}

                {/* Status */}
                {/* <div
                    style={{
                        marginTop: 10,
                        padding: "6px 14px",
                        borderRadius: 14,
                        // background: "#b6bce2",
                        fontSize: 12
                    }}
                >
                    {displayLabel}
                </div> */}
            </div>
        </div>
    );
}
